import {
  arrivalsAddPreview,
  arrivalsPreview,
  getArrivalsArrivalsQueryKey,
  getArrivalsDetailQueryKey,
  getArrivalsDocumentListUrl,
  getArrivalsOptionsQueryKey,
  useArrivalsAddRun,
  useArrivalsArrivals,
  useArrivalsCandidates,
  useArrivalsCorrectItem,
  useArrivalsDetail,
  useArrivalsOptions,
  useArrivalsRemoveItem,
  useArrivalsRun,
  useArrivalsSetDay,
} from "@api/generated/arrivals/arrivals";
import {
  type ArrivalDetail,
  type ArrivalIn,
  type ArrivalItemIn,
  ArrivalItemInKind,
  type ArrivalItemsIn,
  type ArrivalOptions,
  type ArrivalRow,
  type ArrivedItem,
  type Candidate,
  type Hit,
  type PlannedArrival,
  type Recorded,
  type ResolvedStatus,
} from "@api/generated/model";
import { usePartnersRecord } from "@api/generated/partners/partners";
import { useRegisterSearch } from "@api/generated/register/register";
import { useUserMe } from "@api/generated/user/user";
import { usePreview } from "@app/preview";
import { useInvalidate } from "@app/queryClient";
import { hasRole, useAutosave, useSeasonFromLink, useSettings } from "@app/workspace";
import { keyColumn, LineLink } from "@features/genealogy/GenotypeParts";
import { keyText } from "@features/genealogy/genotype";
import { joinNames, nextSenderPlot, PROBLEM_LIMITS, pastedItems, pasteProblems, splitNames, TEXT_MAX } from "@features/genebank/arrivals";
import { detailLines, partnerLine } from "@features/genebank/details";
import { kgColumn, PartnerSelect, removeColumn, useKeptViews } from "@features/genebank/parts";
import { hint, t as translate } from "@i18n";
import { QueryFeedback } from "@ui/QueryFeedback";
import { kindColumn } from "@ui/table/columns";
import { YearInput } from "@ui/YearInput";
import "@features/genebank/genebank.css";
import { ResultBanner } from "@features/system/undo";
import { Button, Loader, Modal, NumberInput, SegmentedControl, Select, TagsInput, Textarea, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@ui/BackButton";
import { DateField } from "@ui/DateField";
import { cellText, formatDate, localToday, num, shortTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { RunBar } from "@ui/RunBar";
import { Splitter } from "@ui/Splitter";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { ShownOrderButton, useListOrder } from "@ui/table/listOrder";
import type { TFunction } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/genebank/arrivals")({ component: ArrivalPage });

/** A register line's key. */
type Key = { paz: number; sid: number };
/** A draft item: the API's item, with a row id of its own (rows are added, pasted and removed). */
type DraftItem = ArrivalItemIn & { row: number };
/** A recorded arrival the items are added to (the legacy picked an existing Arrival ID). */
type Into = { id: number; trial: string | null; institution: string | null; day: string | null };
/** A recorded arrival's top part on its items' Excel file and printout: its number, sender, date and trial. */
const arrivalLines = (t: TFunction, a: Into) =>
  detailLines([
    [t("arrivals.number"), a.id],
    [t("arrivals.sender"), a.institution],
    [t("arrivals.date"), formatDate(a.day)],
    [t("shipments.trial"), a.trial],
  ]);
type Draft = {
  caz: number | null;
  day: string;
  pgm: string;
  gh: string;
  items: DraftItem[];
  into: Into | null;
};
/** What the preview is asked: a new arrival, or items for a recorded one. */
type Asked = { into: null; body: ArrivalIn } | { into: number; body: ArrivalItemsIn };

const empty = (): Draft => ({ caz: null, day: localToday(), pgm: "BW", gh: "W", items: [], into: null });
let rowIds = Date.now();
const newRow = (p: Partial<ArrivalItemIn> = {}): DraftItem => ({ row: rowIds++, ...p });
const NONE: string[] = [];
// the arrivals list's length: the newest ones (the API's ``ARRIVAL_LIST``)
const LISTED = 300;

/** Génbank és magposta → Magérkezés (the legacy Mage): seed a partner sent, item by item identified as a register line
 *  (or new material), recorded with its trial ERK<id> in the working season (kept as a draft per user); more items can
 *  be added to a recorded arrival and its trial. */
function ArrivalPage() {
  const settings = useSettings();
  if (!settings) return <Loader size="sm" m="md" />;
  const stored = settings.arrival_draft?.spec as Draft | undefined;
  return <ArrivalEditor stored={stored ? { ...empty(), ...stored } : null} />;
}

function ArrivalEditor({ stored }: { stored: Draft | null }) {
  const { t } = useTranslation();
  const canEdit = hasRole(useUserMe().data?.roles, "genbank");
  const season = useSeasonFromLink(undefined);
  const client = useQueryClient();
  const options = useArrivalsOptions().data;
  const [draft, setDraft] = useState<Draft>(stored ?? empty());
  const habits = useMemo(() => ({ pgms: options?.pgms ?? NONE, ghs: options?.ghs ?? NONE, pgm: draft.pgm }), [options, draft.pgm]);
  const { savedAt, saveNow } = useAutosave("arrival_draft", { spec: draft }, { spec: stored ?? undefined });
  const [pasting, setPasting] = useState(false);
  const [history, setHistory] = useState(false);
  const [recorded, setRecorded] = useState<(Recorded & { added: boolean }) | null>(null);
  // the next arrival being put together: the last one's banner goes
  const upd = useCallback((p: Partial<Draft>) => {
    setRecorded(null);
    setDraft((d) => ({ ...d, ...p }));
  }, []);
  const setSender = useCallback((caz: number | null) => upd({ caz }), [upd]);
  // the sender's record (the one the sender field reads), for the items' Excel file and printout
  const sender = usePartnersRecord(draft.caz ?? 0, { query: { enabled: draft.caz != null } }).data?.values;
  const setItem = useCallback(
    (row: number, p: Partial<ArrivalItemIn>) =>
      setDraft((d) => ({ ...d, items: d.items.map((x) => (x.row === row ? { ...x, ...p } : x)) })),
    [],
  );
  const removeItem = useCallback((row: number) => setDraft((d) => ({ ...d, items: d.items.filter((x) => x.row !== row) })), []);
  const addItems = (items: Partial<ArrivalItemIn>[]) => {
    setRecorded(null);
    setDraft((d) => ({ ...d, items: [...d.items, ...items.map(newRow)] }));
  };
  const common: ArrivalItemsIn | null =
    season != null && draft.items.length
      ? {
          ev: season,
          pgm: draft.pgm || null,
          gh: draft.gh || null,
          items: draft.items.map(({ row: _r, ...it }) => it),
        }
      : null;
  const body: Asked | null = !common
    ? null
    : draft.into
      ? { into: draft.into.id, body: common }
      : draft.caz != null && draft.day
        ? { into: null, body: { ...common, caz: draft.caz, day: draft.day } }
        : null;
  // the preview asks only what identifies the items: typing weights does not ask again
  const asked = body && {
    ...body,
    body: {
      ...body.body,
      items: body.body.items.map((it) => ({
        name: it.name,
        kind: it.kind,
        line: it.line,
        new: it.new,
        other_names: it.other_names,
        pgm: it.pgm,
        gh: it.gh,
      })),
    },
  };
  const preview = usePreview("arrival-preview", asked as Asked | null, (a, signal) =>
    a.into == null ? arrivalsPreview(a.body, { signal }) : arrivalsAddPreview(a.into, a.body, { signal }),
  );
  const plan = preview.data;
  const rows = plan && !preview.isPlaceholderData && plan.rows.length === draft.items.length ? plan.rows : null;
  // the items' problems by kind: no kind and to confirm have requirements of their own, the rest "error-free items"
  const problems = new Map<string, number>();
  for (const r of rows ?? []) if (r.resolved.problem) problems.set(r.resolved.problem, (problems.get(r.resolved.problem) ?? 0) + 1);
  const unconfirmed = problems.get("unconfirmed") ?? 0;
  const unkinded = problems.get("no_kind") ?? 0;
  const otherProblems = [...problems.values()].reduce((a, b) => a + b, 0) - unconfirmed - unkinded;
  // an item's place in the draft (its plot number's order) and what the preview made of it; a view stays the same
  // object while its item does (a keystroke redraws only the row typed in)
  const keep = useKeptViews<DraftItem, ItemView>();
  // what an item not identified yet waits for: nothing is asked without the sender and the date, or the preview failed
  const waiting: Waiting =
    !draft.into && draft.caz == null ? "sender" : !draft.into && !draft.day ? "date" : preview.error ? "error" : null;
  const items = useMemo(
    () => draft.items.map((it, i) => keep(it, { it, n: i + 1, planned: rows?.[i], waiting })),
    [draft.items, rows, keep, waiting],
  );
  // the items' order is the plots' order in the ERK trial: moved here, or the table's sort kept
  const setItems = useCallback((f: (items: DraftItem[]) => DraftItem[]) => setDraft((d) => ({ ...d, items: f(d.items) })), []);
  const order = useListOrder({ setItems, place: placeOf, itemOf, enabled: canEdit });
  const own = useItemColumns(habits, setItem, removeItem);
  const columns = useMemo(() => (order.column ? [order.column, ...own] : own), [own, order.column]);
  const table = useDataTable("genebank.arrivals.items", columns, items);
  const done = (out: Recorded) => {
    const added = draft.into != null;
    setRecorded({ ...out, added });
    const next = { ...empty(), caz: draft.caz, pgm: draft.pgm, gh: draft.gh };
    setDraft(next);
    saveNow({ spec: next });
    if (!added)
      client.setQueryData<ArrivalOptions>(getArrivalsOptionsQueryKey(), (o) => (o && out.id != null ? { ...o, next_id: out.id + 1 } : o));
    void client.invalidateQueries({ queryKey: [getArrivalsArrivalsQueryKey()[0]] });
    if (out.id != null) void client.invalidateQueries({ queryKey: getArrivalsDetailQueryKey(out.id) });
  };
  const run = useArrivalsRun({ mutation: { onSuccess: done } });
  const addRun = useArrivalsAddRun({ mutation: { onSuccess: done } });
  const onRun = () => {
    if (!body || !plan) return;
    // an abbreviation not typed is the one the preview suggests (a hyphenated variety); cleared, there is none
    const items = body.body.items.map((it, i) => ({
      ...it,
      abbreviation: it.abbreviation === undefined ? (plan.rows[i]?.abbreviation ?? null) : it.abbreviation || null,
    }));
    if (body.into == null) run.mutate({ data: { ...body.body, items, token: plan.token } });
    else addRun.mutate({ arrival: body.into, data: { ...body.body, items, token: plan.token } });
  };
  const senderMissing = plan && !preview.isPlaceholderData ? plan.sender_missing : [];
  // the top part on the items' Excel file and printout: which arrival, from whom, when
  const about = draft.into
    ? arrivalLines(t, draft.into)
    : detailLines([
        [t("arrivals.number"), options?.next_id],
        [t("arrivals.sender"), partnerLine(sender)],
        [t("arrivals.date"), draft.day && formatDate(draft.day)],
        ["PGM", draft.pgm],
        ["GH", draft.gh],
      ]);
  if (season == null) return <Loader size="sm" m="md" />;
  return (
    <div className="page fill">
      <PageHeader
        title={t("arrivals.title")}
        subtitle={
          draft.into
            ? t("arrivals.addToTitle", { id: draft.into.id, kis: draft.into.trial ?? "—" })
            : options
              ? t("arrivals.subtitle", { id: options.next_id })
              : undefined
        }
        season
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            {savedAt && <span className="sm mu">{t("shipments.draftSaved", { time: shortTime(savedAt.toISOString()) })}</span>}
            <Button size="compact-sm" variant="default" onClick={() => setHistory(true)}>
              {t("arrivals.previous")}
            </Button>
          </div>
        }
      />
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <section className="card pad">
        <div className="hstack" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          {draft.into ? (
            <div className="sm stack" style={{ gap: 4, maxWidth: 360 }}>
              <b>
                № {draft.into.id} · {draft.into.institution ?? "—"} · {formatDate(draft.into.day)} · {draft.into.trial ?? "—"}
              </b>
              <span className="mu">{t("arrivals.addToHelp")}</span>
              <Button size="compact-xs" variant="default" onClick={() => upd({ into: null })}>
                {t("arrivals.newArrival")}
              </Button>
            </div>
          ) : (
            <>
              <PartnerSelect inline w={300} label={t("arrivals.sender")} value={draft.caz} onChange={setSender} />
              <DateField
                size="xs"
                clearable={false}
                w={150}
                label={t("arrivals.date")}
                value={draft.day}
                onChange={(day) => upd({ day })}
              />
            </>
          )}
          <Select
            placeholder={hint("searchSelect")}
            size="xs"
            w={100}
            label="PGM"
            searchable
            value={draft.pgm || null}
            onChange={(v) => upd({ pgm: v ?? "" })}
            data={options?.pgms ?? NONE}
          />
          <Select
            placeholder={hint("searchSelect")}
            size="xs"
            w={90}
            label="GH"
            searchable
            value={draft.gh || null}
            onChange={(v) => upd({ gh: v ?? "" })}
            data={options?.ghs ?? NONE}
          />
        </div>
        {senderMissing.length > 0 && (
          <div className="note warn sm" style={{ marginTop: 10 }}>
            {t("arrivals.senderMissing", { fields: senderMissing.map((f) => t(`partners.field.${f}`)).join(", ") })}
          </div>
        )}
      </section>
      <div className="fill-row">
        <section className="card stack grow" style={{ gap: 0, minWidth: 0 }}>
          <div className="hstack panel-head" style={{ gap: 8 }}>
            <h2 className="h2 grow">{t("arrivals.items")}</h2>
            <Button size="compact-sm" variant="default" disabled={!canEdit} onClick={() => setPasting(true)}>
              {t("arrivals.paste")}
            </Button>
            <Button size="compact-sm" variant="default" disabled={!canEdit} onClick={() => addItems([nextSenderPlot(draft.items.at(-1))])}>
              {t("arrivals.newItem")}
            </Button>
            <ShownOrderButton table={table} disabled={!canEdit} onOrder={() => order.keepShown(table.rows)} />
          </div>
          <DataTable
            table={table}
            rowKey={(x) => x.it.row}
            rowProps={order.rowProps}
            className="grow"
            empty={t("arrivals.empty")}
            about={about}
          />
          <div className="inset">
            <RunBar
              requirements={[
                { label: t("shipments.needPartner"), done: draft.into != null || draft.caz != null },
                { label: t("arrivals.needDate"), done: draft.into != null || !!draft.day },
                { label: t("shipments.needItem"), done: draft.items.length > 0 },
                { label: t("arrivals.needKind", { n: unkinded }), done: rows != null && unkinded === 0 },
                { label: t("arrivals.needConfirmed", { n: unconfirmed }), done: rows != null && unconfirmed === 0 },
                { label: t("arrivals.needValidItems"), done: otherProblems === 0 },
                { label: t("shipments.needPermission"), done: canEdit },
              ]}
              summary={
                plan &&
                t("arrivals.summary", {
                  combinations: plan.counts.combinations,
                  lines: plan.counts.lines,
                  n: plan.counts.items,
                  kis: plan.trial ?? "ERK…",
                  ev: season,
                })
              }
              undoable
              busy={preview.busy || !rows}
              run={{
                label: draft.into ? t("arrivals.addRecord", { n: draft.items.length }) : t("arrivals.record", { n: draft.items.length }),
                loading: run.isPending || addRun.isPending,
                onClick: onRun,
              }}
            />
          </div>
        </section>
        <Splitter cssVar="--gb-side-w" side="end" min={180} max={480} />
        <aside className="card pad stack gb-side">
          <h2 className="h2">{t("arrivals.createdOnSave")}</h2>
          <dl className="gb-counts">
            <dt>{t("arrivals.newCombination")}</dt>
            <dd>{plan?.counts.combinations ?? "—"}</dd>
            <dt>{t("arrivals.newLine")}</dt>
            <dd>{plan?.counts.lines ?? "—"}</dd>
            <dt>{t("arrivals.plotsAndRows", { kis: plan?.trial ?? "ERK…", ev: season })}</dt>
            <dd>{plan?.counts.items ?? "—"}</dd>
          </dl>
          <p className="sm mu">{t("arrivals.seedNote")}</p>
          {recorded?.id != null && (
            <ResultBanner
              operation={recorded.operation}
              actions={
                <a className="sm" href={getArrivalsDocumentListUrl(recorded.id)}>
                  {t("arrivals.listDownload", { id: recorded.id })}
                </a>
              }
            >
              <b>
                {recorded.added
                  ? t("arrivals.added", { id: recorded.id, n: recorded.written })
                  : t("arrivals.recorded", { id: recorded.id, n: recorded.written })}
              </b>
            </ResultBanner>
          )}
        </aside>
      </div>
      {pasting && (
        <PasteModal
          onAdd={(items) => {
            addItems(items);
            setPasting(false);
          }}
          onClose={() => setPasting(false)}
        />
      )}
      <HistoryModal
        opened={history}
        onClose={() => setHistory(false)}
        onAddItems={
          canEdit
            ? (d) => {
                upd({
                  into: { id: d.row.id, trial: d.trial ?? null, institution: d.row.institution ?? null, day: d.row.day ?? null },
                  caz: d.row.caz ?? draft.caz,
                });
                setHistory(false);
              }
            : undefined
        }
      />
    </div>
  );
}

/** The PGM and growth-habit codes an item can have of its own, and the arrival's PGM (an item's unless its own). */
type Habits = { pgms: string[]; ghs: string[]; pgm: string };

/** A draft item as the table shows it: its place in the draft (1-based) and the preview's reading of it. */
type ItemView = { it: DraftItem; n: number; planned: PlannedArrival | undefined; waiting: Waiting };
/** Why the items are not identified: no sender, no date (nothing is asked), the preview failed; null: being asked. */
type Waiting = "sender" | "date" | "error" | null;
const placeOf = (x: ItemView) => x.n - 1;
const itemOf = (x: ItemView) => x.it;

/** The draft items' columns: every field an input (the rows keep their draft's identity when sorted). */
function useItemColumns(habits: Habits, onChange: (row: number, p: Partial<ArrivalItemIn>) => void, onRemove: (row: number) => void) {
  const { t } = useTranslation();
  return useMemo<Column<ItemView>[]>(() => {
    const identity = ({ it, planned }: ItemView) => {
      const r = planned?.resolved;
      if (!r || (!it.name?.trim() && !it.line)) return null;
      if (r.problem) return t(`arrivals.problems.${r.problem}`);
      return t(`arrivals.status.${r.status}`, {
        name: planned?.name ?? "—",
        paz: r.line?.paz,
        sid: r.line?.sid,
        group: (r.group ?? 0) + 1,
      });
    };
    return [
      {
        id: "n",
        header: "#",
        value: (x) => x.n,
        numeric: true,
        cellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        cell: ({ it, n }) => (
          <>
            <button type="button" className="chip" aria-label={t("arrivals.deleteItem", { n })} onClick={() => onRemove(it.row)}>
              ✕
            </button>{" "}
            {n}
          </>
        ),
      },
      {
        id: "sender_exp",
        header: t("arrivals.senderTrial"),
        value: (x) => x.it.sender_exp,
        cell: ({ it }) => (
          <TextInput
            size="xs"
            w={80}
            aria-label={t("arrivals.senderTrial")}
            value={it.sender_exp ?? ""}
            maxLength={TEXT_MAX.sender_exp}
            onChange={(e) => onChange(it.row, { sender_exp: e.currentTarget.value || null })}
          />
        ),
      },
      {
        id: "sender_plot",
        header: t("arrivals.senderPlot"),
        value: (x) => x.it.sender_plot,
        numeric: true,
        cell: ({ it }) => (
          <NumberInput
            size="xs"
            w={64}
            hideControls
            allowDecimal={false}
            aria-label={t("arrivals.senderPlot")}
            value={it.sender_plot ?? ""}
            onChange={(v) => onChange(it.row, { sender_plot: num(v) })}
          />
        ),
      },
      {
        id: "sender_year",
        header: t("arrivals.senderYear"),
        title: t("arrivals.senderYearHelp"),
        value: (x) => x.it.sender_year,
        numeric: true,
        cell: ({ it }) => (
          <YearInput
            size="xs"
            w={64}
            hideControls
            placeholder={t("arrivals.optional")}
            aria-label={t("arrivals.senderYear")}
            value={it.sender_year ?? ""}
            onChange={(v) => onChange(it.row, { sender_year: num(v) })}
          />
        ),
      },
      {
        id: "name",
        header: t("arrivals.name"),
        value: (x) => x.it.name,
        cell: ({ it }) => (
          <TextInput
            size="xs"
            w={160}
            className="mono"
            placeholder={t("arrivals.nameHelp")}
            aria-label={t("arrivals.name")}
            value={it.name ?? ""}
            maxLength={TEXT_MAX.name}
            // capitals only (the breeders' rule; the legacy typed them so); a new name is identified again and drops
            // the abbreviation typed
            onChange={(e) =>
              onChange(it.row, { name: e.currentTarget.value.toUpperCase(), line: null, new: false, abbreviation: undefined })
            }
          />
        ),
      },
      {
        id: "kind",
        header: t("arrivals.kindHeader"),
        title: t("arrivals.kindHelp"),
        value: (x) => (x.it.kind ? t(`arrivals.kind.${x.it.kind}`) : null),
        cell: ({ it }) => <KindChoice value={it.kind} onChange={(kind) => onChange(it.row, { kind })} />,
      },
      {
        id: "other_names",
        header: t("arrivals.otherNames"),
        title: t("arrivals.otherNamesHelp"),
        value: (x) => joinNames(x.it.other_names),
        cell: ({ it }) => <OtherNamesInput value={it.other_names} onChange={(other_names) => onChange(it.row, { other_names })} />,
      },
      {
        id: "abbreviation",
        header: t("arrivals.abbreviation"),
        value: (x) => abbreviationOf(x),
        cell: (x) => (
          <TextInput
            size="xs"
            w={100}
            className="mono"
            aria-label={t("arrivals.abbreviation")}
            disabled={!x.planned || x.planned.resolved.status === "existing"}
            value={abbreviationOf(x)}
            maxLength={TEXT_MAX.abbreviation}
            onChange={(e) => onChange(x.it.row, { abbreviation: e.currentTarget.value.toUpperCase() })}
          />
        ),
      },
      {
        id: "identification",
        header: t("arrivals.identification"),
        value: identity,
        cell: ({ it, planned, waiting }) => (
          <div className="stack" style={{ gap: 4 }}>
            <Identity it={it} planned={planned} waiting={waiting} habits={habits} onChange={(p) => onChange(it.row, p)} />
            {!!planned?.warnings.length && (
              <span className="chip y" style={{ whiteSpace: "normal", maxWidth: 220, display: "inline-block" }}>
                {t("arrivals.nameRules", { rules: planned.warnings.map((w) => t(`arrivals.nameWarning.${w}`)).join(", ") })}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "kg",
        header: t("arrivals.weight"),
        value: (x) => x.it.kg,
        numeric: true,
        cell: ({ it }) => (
          <NumberInput
            size="xs"
            w={70}
            hideControls
            min={0}
            aria-label={t("arrivals.weight")}
            value={it.kg ?? ""}
            onChange={(v) => onChange(it.row, { kg: num(v) })}
          />
        ),
      },
      {
        id: "tkw",
        header: t("arrivals.tkw"),
        title: t("arrivals.tkwHelp"),
        value: (x) => x.it.tkw,
        numeric: true,
        cell: ({ it }) => (
          <NumberInput
            size="xs"
            w={56}
            hideControls
            min={0}
            aria-label={t("arrivals.tkw")}
            value={it.tkw ?? ""}
            onChange={(v) => onChange(it.row, { tkw: num(v) })}
          />
        ),
      },
      {
        id: "germ",
        header: t("shipments.germination"),
        value: (x) => x.it.germ,
        numeric: true,
        cell: ({ it }) => (
          <NumberInput
            size="xs"
            w={48}
            hideControls
            min={0}
            max={100}
            aria-label={t("shipments.germination")}
            value={it.germ ?? ""}
            onChange={(v) => onChange(it.row, { germ: num(v) })}
          />
        ),
      },
      {
        id: "remark",
        header: t("arrivals.remark"),
        value: (x) => x.it.remark,
        cell: ({ it }) => (
          <TextInput
            size="xs"
            w={130}
            aria-label={t("arrivals.remark")}
            value={it.remark ?? ""}
            maxLength={TEXT_MAX.remark}
            onChange={(e) => onChange(it.row, { remark: e.currentTarget.value || null })}
          />
        ),
      },
    ];
  }, [t, habits, onChange, onRemove]);
}

/** The new line's abbreviation as typed, else the one the preview suggests (an existing line keeps its own). */
const abbreviationOf = ({ it, planned }: ItemView) =>
  it.abbreviation !== undefined ? (it.abbreviation ?? "") : (planned?.abbreviation ?? "");

const KINDS = Object.values(ArrivalItemInKind);

const STATUS_CHIP: Record<ResolvedStatus, "g" | "y" | "b"> = { existing: "g", new: "y", same_new: "y" };

/** What a name is (variety, line name, pedigree): always chosen, never guessed; marked while not chosen. */
function KindChoice({ value, onChange }: { value: ArrivalItemInKind | null | undefined; onChange: (kind: ArrivalItemInKind) => void }) {
  const { t } = useTranslation();
  return (
    <SegmentedControl
      size="xs"
      aria-label={t("arrivals.kindHeader")}
      className={value ? undefined : "gb-needs-choice"}
      value={value ?? ""}
      onChange={(v) => onChange(v as ArrivalItemInKind)}
      data={KINDS.map((k) => ({ value: k, label: t(`arrivals.kind.${k}`) }))}
    />
  );
}

/** Other names as tags: typed and closed with a comma, semicolon or Enter (a name may have spaces), in capitals. */
function OtherNamesInput({ value, onChange, label }: { value: string[] | undefined; onChange: (names: string[]) => void; label?: string }) {
  const { t } = useTranslation();
  return (
    <TagsInput
      size="xs"
      w={label ? undefined : 180}
      className="mono"
      label={label}
      description={label ? t("arrivals.otherNamesHelp") : undefined}
      placeholder={value?.length ? undefined : t("arrivals.optional")}
      aria-label={t("arrivals.otherNames")}
      value={value ?? []}
      onChange={(v) => onChange(splitNames(v.join(",")))}
      splitChars={[",", ";", "\n"]}
      acceptValueOnBlur
    />
  );
}

/** A table's first column: the button that confirms the row's line, or "chosen" on the line already chosen. */
function pickColumn<T>(line: (row: T) => Key, chosen: Key | null | undefined, onPick: (line: Key) => void): Column<T> {
  return {
    id: "pick",
    header: "",
    fixed: true,
    cell: (row) =>
      chosen?.paz === line(row).paz && chosen?.sid === line(row).sid ? (
        <span className="chip g">{translate("arrivals.chosen")}</span>
      ) : (
        <Button size="compact-xs" onClick={() => onPick(line(row))}>
          {translate("arrivals.thisIsIt")}
        </Button>
      ),
  };
}

function IdentityChip({ it, planned, waiting }: { it: DraftItem; planned: PlannedArrival | undefined; waiting: Waiting }) {
  const { t } = useTranslation();
  const r = planned?.resolved;
  if (!it.name?.trim() && !it.line) return <span className="sm mu">{t("arrivals.searchAsYouType")}</span>;
  if (!r) return waiting ? <span className="sm mu">{t(`arrivals.waiting.${waiting}`)}</span> : <Loader size={12} />;
  if (r.problem)
    return (
      <span className={`chip ${r.problem === "unconfirmed" ? "y" : "r"}`}>
        {t(`arrivals.problems.${r.problem}`, { n: planned?.found ?? 0 })}
      </span>
    );
  const values = { name: planned?.name ?? "—", paz: r.line?.paz, sid: r.line?.sid, group: (r.group ?? 0) + 1 };
  const text = t(`arrivals.status.${r.status}`, values);
  // one line, as the row's other cells (a column made narrower cuts it with …; the whole text in the tooltip)
  return (
    <span
      className={`chip ${STATUS_CHIP[r.status]}`}
      title={text}
      style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
    >
      {text}
    </span>
  );
}

/** How the item is identified (from the preview) and the button that confirms or changes it. */
function Identity({
  it,
  planned,
  waiting,
  habits,
  onChange,
}: {
  it: DraftItem;
  planned: PlannedArrival | undefined;
  waiting: Waiting;
  habits: Habits;
  onChange: (p: Partial<ArrivalItemIn>) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const toConfirm = planned?.resolved.problem === "unconfirmed";
  const chosen = it.line != null || !!it.new;
  const named = !!it.name?.trim() || !!it.other_names?.length;
  return (
    <div className="hstack" style={{ gap: 6, flexWrap: "nowrap", minWidth: 0 }}>
      <IdentityChip it={it} planned={planned} waiting={waiting} />
      {(named || it.line) && (
        <Button
          style={{ flexShrink: 0 }}
          size="compact-xs"
          variant={toConfirm ? "filled" : "light"}
          color={toConfirm ? "yellow" : undefined}
          onClick={() => setOpen(true)}
        >
          {toConfirm ? t("arrivals.confirm") : chosen ? t("arrivals.changeIdentification") : t("arrivals.identify")}
        </Button>
      )}
      {(it.pgm || it.gh) && (
        <span className="chip mono" style={{ flexShrink: 0 }}>
          {it.pgm ?? "·"}/{it.gh ?? "·"}
        </span>
      )}
      {open && <IdentifyModal it={it} habits={habits} onChange={onChange} onClose={close} />}
    </div>
  );
}

/** The register lines an item may be (its names, names containing them, names differing in a character or two), each
 *  with a button that confirms it; new material; a search of the whole register; the item's own PGM and type. */
function IdentifyModal({
  it,
  habits,
  onChange,
  onClose,
}: {
  it: DraftItem;
  habits: Habits;
  onChange: (p: Partial<ArrivalItemIn>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const pick = useCallback(
    (line: { paz: number; sid: number }) => {
      onChange({ line, new: false });
      onClose();
    },
    [onChange, onClose],
  );
  const chosen = it.line;
  // the item's names searched in the register (its PGM, else the arrival's)
  const found = useArrivalsCandidates({
    name: it.name?.trim() || undefined,
    other: it.other_names?.length ? it.other_names : undefined,
    pgm: it.pgm || habits.pgm || undefined,
  }).data;
  const columns = useMemo<Column<Candidate>[]>(
    () => [
      pickColumn((c: Candidate) => c.line, chosen, pick),
      {
        id: "match",
        header: t("arrivals.match"),
        value: (c) => t(`arrivals.matchKind.${c.match}`, { n: c.distance }),
        cell: (c) => (
          <span className={`chip ${c.match === "exact" ? "g" : c.match === "similar" ? "y" : "b"}`}>
            {t(`arrivals.matchKind.${c.match}`, { n: c.distance })}
          </span>
        ),
      },
      keyColumn((c: Candidate) => c.line, "line"),
      { id: "name", header: t("arrivals.lineName"), value: (c) => c.name },
      {
        id: "matched",
        header: t("arrivals.matchedName"),
        value: (c) => c.matched,
        cell: (c) => (
          <span>
            <span className="mono">{c.matched}</span> <span className="sm mu">({t(`arrivals.field.${c.field}`)})</span>
          </span>
        ),
      },
      { id: "pedigree", header: t("arrivals.pedigree"), value: (c) => c.pedigree, className: "sm mono" },
      { id: "pgm", header: "PGM/GH", value: (c) => `${c.pgm ?? "·"}/${c.gh ?? "·"}`, className: "mono" },
    ],
    [t, chosen, pick],
  );
  const table = useDataTable("genebank.arrivals.candidates", columns, found);
  const names = [it.name, ...(it.other_names ?? [])].filter(Boolean).join(" · ");
  return (
    <Modal opened onClose={onClose} title={t("arrivals.identifyTitle", { name: names || "—" })} size="xl">
      <div className="stack" style={{ gap: 14 }}>
        <section className="stack" style={{ gap: 6 }}>
          <h3 className="h3">{t("arrivals.foundInRegister")}</h3>
          <p className="sm mu">{t("arrivals.foundHelp")}</p>
          {!found ? (
            <Loader size="sm" />
          ) : (
            <div className="stack" style={{ maxHeight: 300, minHeight: 0 }}>
              <DataTable table={table} rowKey={(c) => keyText(c.line)} empty={t("arrivals.nothingFound")} />
            </div>
          )}
        </section>
        <section className="hstack" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {it.new ? (
            <span className="chip y">{t("arrivals.newChosen")}</span>
          ) : (
            <Button
              variant="outline"
              color="yellow"
              onClick={() => {
                onChange({ new: true, line: null });
                onClose();
              }}
            >
              {t("arrivals.newMaterial")}
            </Button>
          )}
          <span className="sm mu grow">{t("arrivals.newMaterialHelp")}</span>
          {(it.line || it.new) && (
            <Button variant="default" onClick={() => onChange({ line: null, new: false })}>
              {t("arrivals.undoChoice")}
            </Button>
          )}
        </section>
        <RegisterSearch q={it.name ?? ""} chosen={chosen} onPick={pick} />
        <section className="stack" style={{ gap: 6 }}>
          <h3 className="h3">{t("arrivals.ownPgm")}</h3>
          <div className="hstack" style={{ gap: 6 }}>
            <Select
              size="xs"
              w={110}
              searchable
              clearable
              aria-label="PGM"
              label="PGM"
              placeholder={t("arrivals.ownPgmHelp")}
              value={it.pgm ?? null}
              onChange={(v) => onChange({ pgm: v })}
              data={habits.pgms}
            />
            <Select
              size="xs"
              w={110}
              searchable
              clearable
              aria-label="GH"
              label="GH"
              placeholder={t("arrivals.ownPgmHelp")}
              value={it.gh ?? null}
              onChange={(v) => onChange({ gh: v })}
              data={habits.ghs}
            />
          </div>
        </section>
      </div>
    </Modal>
  );
}

/** The whole register searched (name, pedigree, PAz SID), each hit with a button that confirms it. */
function RegisterSearch({ q: initial, chosen, onPick }: { q: string; chosen: Key | null | undefined; onPick: (line: Key) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState(initial);
  const [asked] = useDebouncedValue(q.trim(), 300);
  const found = useRegisterSearch({ q: asked, field: "all", limit: 40 }, { query: { enabled: asked.length >= 2 } }).data;
  const columns = useMemo<Column<Hit>[]>(
    () => [
      pickColumn((h: Hit) => h.key, chosen, onPick),
      keyColumn((h: Hit) => h.key, "line"),
      { id: "name", header: t("arrivals.name"), value: (h) => h.name },
      { id: "pedigree", header: t("arrivals.pedigree"), value: (h) => h.pedigree, className: "sm mono" },
      { id: "pgm", header: "PGM", value: (h) => h.pgm },
    ],
    [t, chosen, onPick],
  );
  // the first 40 hits (``truncated``: there are more)
  const table = useDataTable("genebank.arrivals.lines", columns, found?.hits, { partial: !!found?.truncated });
  return (
    <section className="stack" style={{ gap: 6 }}>
      <h3 className="h3">{t("arrivals.searchRegister")}</h3>
      <TextInput size="xs" label={t("arrivals.search")} value={q} onChange={(e) => setQ(e.currentTarget.value)} />
      {asked.length >= 2 && (
        <div className="stack" style={{ maxHeight: 240, minHeight: 0 }}>
          <DataTable table={table} rowKey={(h) => keyText(h.key)} />
        </div>
      )}
    </section>
  );
}

function PasteModal({ onAdd, onClose }: { onAdd: (items: Partial<ArrivalItemIn>[]) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [kind, setKind] = useState<ArrivalItemInKind | null>(null);
  // the rows the server would refuse are listed (row number, the fields) and not added
  const pasted = pastedItems(text).map((it, i) => ({ it, n: i + 1, problems: pasteProblems(it) }));
  const items = pasted.filter((p) => !p.problems.length).map((p) => p.it);
  const refused = pasted.filter((p) => p.problems.length);
  return (
    <Modal opened onClose={onClose} title={t("arrivals.paste")} size="lg">
      <div className="stack" style={{ gap: 10 }}>
        <p className="sm mu">{t("arrivals.pasteHelp")}</p>
        <Textarea
          autosize
          minRows={8}
          maxRows={16}
          className="mono"
          aria-label={t("arrivals.paste")}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          data-autofocus
        />
        <div className="stack" style={{ gap: 4 }}>
          <span className="sm">
            <b>{t("arrivals.pastedKind")}</b> <span className="mu">{t("arrivals.pastedKindHelp")}</span>
          </span>
          <KindChoice value={kind} onChange={setKind} />
        </div>
        {refused.length > 0 && (
          <div className="note warn sm stack" style={{ gap: 2 }}>
            <b>{t("arrivals.pasteRefused", { n: refused.length })}</b>
            {refused.map((p) => (
              <span key={p.n}>
                {t("arrivals.pasteRow", { n: p.n, name: p.it.name })}:{" "}
                {p.problems.map((f) => t(`arrivals.pasteProblem.${f}`, PROBLEM_LIMITS[f])).join("; ")}
              </span>
            ))}
          </div>
        )}
        <Button disabled={!items.length || !kind} onClick={() => onAdd(items.map((it) => ({ ...it, kind: kind ?? undefined })))}>
          {t("arrivals.addPasted", { n: items.length })}
        </Button>
      </div>
    </Modal>
  );
}

function HistoryModal({
  opened,
  onClose,
  onAddItems,
}: {
  opened: boolean;
  onClose: () => void;
  onAddItems?: (detail: ArrivalDetail) => void;
}) {
  const { t } = useTranslation();
  const list = useArrivalsArrivals(undefined, { query: { enabled: opened } }).data;
  const [open, setOpen] = useState<number | null>(null);
  const columns = useMemo<Column<ArrivalRow>[]>(
    () => [
      { id: "id", header: "№", value: (a) => a.id, text: (a) => String(a.id), numeric: true, className: "mono" },
      { id: "day", header: t("arrivals.date"), value: (a) => a.day, text: (a) => formatDate(a.day) },
      { id: "sender", header: t("arrivals.sender"), value: (a) => a.institution },
      { id: "items", header: t("shipments.item"), value: (a) => a.items, numeric: true },
      kgColumn((a) => a.kg),
    ],
    [t],
  );
  // the newest arrivals only: a full list may have older ones
  const table = useDataTable("genebank.arrivals.history", columns, list, { partial: (list?.length ?? 0) >= LISTED });
  return (
    <Modal opened={opened} onClose={onClose} title={t("arrivals.previous")} size="xl">
      {open != null ? (
        <ArrivalView id={open} onBack={() => setOpen(null)} onAddItems={onAddItems} />
      ) : (
        <div className="stack" style={{ maxHeight: 520, minHeight: 0 }}>
          <DataTable table={table} rowKey={(a) => a.id} rowProps={(a) => ({ className: "click", onClick: () => setOpen(a.id) })} />
        </div>
      )}
    </Modal>
  );
}

function ArrivalView({ id, onBack, onAddItems }: { id: number; onBack: () => void; onAddItems?: (detail: ArrivalDetail) => void }) {
  const { t } = useTranslation();
  const detail = useArrivalsDetail(id).data;
  const canEdit = hasRole(useUserMe().data?.roles, "genbank");
  const changed = useInvalidate(/^\/api\/v1\/(shipments|arrivals|genebank|years|measurements|audit)/);
  const { mutate: removeItem, isPending: removing } = useArrivalsRemoveItem({
    mutation: {
      onSuccess: () => {
        changed();
        onBack();
      },
    },
  });
  const [editing, setEditing] = useState<ArrivedItem | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const setDayRun = useArrivalsSetDay({
    mutation: {
      onSuccess: () => {
        changed();
        setDay(null);
      },
    },
  });
  const columns = useMemo<Column<ArrivedItem>[]>(
    () => [
      { id: "parc", header: "#", value: (it) => it.parc, text: (it) => cellText(it.parc), numeric: true },
      { id: "sender_exp", header: t("arrivals.senderTrial"), value: (it) => it.sender_exp, className: "mono sm" },
      {
        id: "sender_plot",
        header: t("arrivals.senderPlot"),
        value: (it) => it.sender_plot,
        text: (it) => cellText(it.sender_plot),
        numeric: true,
        className: "mono sm",
      },
      kindColumn("integer", (it: ArrivedItem) => it.sender_year, {
        id: "sender_year",
        header: t("arrivals.senderYear"),
        className: "mono sm",
      }),
      {
        id: "name",
        header: t("arrivals.name"),
        value: (it) => it.name,
        cell: (it) =>
          it.paz != null && it.sid != null ? <LineLink line={{ key: { paz: it.paz, sid: it.sid }, name: it.name }} /> : (it.name ?? "—"),
      },
      { id: "other_names", header: t("arrivals.otherNames"), value: (it) => joinNames(it.other_names), className: "mono sm" },
      kgColumn((it) => it.kg),
      { id: "tkw", header: t("arrivals.tkw"), value: (it) => it.tkw, numeric: true },
      { id: "germ", header: t("shipments.germination"), value: (it) => it.germ, numeric: true },
      { id: "remark", header: t("arrivals.remark"), value: (it) => it.remark, className: "sm" },
      {
        id: "edit",
        header: "",
        name: t("arrivals.editItem"),
        fixed: true,
        cell: (it) =>
          canEdit && (
            <Button size="compact-xs" variant="light" onClick={() => setEditing(it)}>
              {t("arrivals.editItem")}
            </Button>
          ),
      },
      removeColumn({ canEdit, busy: removing, onRemove: (it) => removeItem({ arrival: id, id: it.id, params: { token: it.token } }) }),
    ],
    [t, canEdit, removing, removeItem, id],
  );
  const table = useDataTable("genebank.arrivals.arrival-items", columns, detail?.items);
  if (!detail) return <Loader size="sm" />;
  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8 }}>
        <BackButton size="compact-sm" onClick={onBack}>
          {t("shipments.back")}
        </BackButton>
        <b className="grow">
          № {detail.row.id} · {formatDate(detail.row.day)} · {detail.row.institution ?? "—"} · {detail.trial}
        </b>
        {canEdit && day == null && (
          <Button size="compact-sm" variant="default" onClick={() => setDay(detail.row.day ?? localToday())}>
            {t("arrivals.editDate")}
          </Button>
        )}
        <a className="sm" href={getArrivalsDocumentListUrl(id)}>
          {t("arrivals.listDownload", { id })}
        </a>
        {onAddItems && (
          <Button size="compact-sm" onClick={() => onAddItems(detail)}>
            {t("arrivals.addItems")}
          </Button>
        )}
      </div>
      {day != null && (
        <div className="hstack note" style={{ gap: 8, alignItems: "flex-end" }}>
          <DateField size="xs" clearable={false} w={150} label={t("arrivals.date")} value={day} onChange={setDay} />
          <Button
            size="compact-sm"
            disabled={!day}
            loading={setDayRun.isPending}
            onClick={() => setDayRun.mutate({ arrival: id, data: { day } })}
          >
            {t("arrivals.saveDate")}
          </Button>
          <Button size="compact-sm" variant="default" onClick={() => setDay(null)}>
            {t("arrivals.cancel")}
          </Button>
        </div>
      )}
      <DataTable
        table={table}
        rowKey={(it) => it.id}
        about={arrivalLines(t, { ...detail.row, institution: partnerLine(detail.sender) ?? detail.row.institution, trial: detail.trial })}
      />
      {editing && (
        <EditItemModal
          arrival={id}
          item={editing}
          onDone={() => {
            changed();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// the seed's measured values in the item's correction: field, label, highest value
const SEED_FIELDS = [
  ["kg", "arrivals.weight", undefined],
  ["tkw", "arrivals.tkw", undefined],
  ["germ", "arrivals.germination", 100],
] as const;

/** A recorded item's data corrected: weight, TKW, germination, the sender's trial, plot and year, other names, remark.
 *  What it is (its line) is not changed here: that is deleting it and adding it again. */
function EditItemModal({
  arrival,
  item,
  onDone,
  onClose,
}: {
  arrival: number;
  item: ArrivedItem;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState({
    kg: item.kg ?? null,
    tkw: item.tkw ?? null,
    germ: item.germ ?? null,
    sender_exp: item.sender_exp ?? "",
    sender_plot: item.sender_plot ?? null,
    sender_year: item.sender_year ?? null,
    other_names: item.other_names,
    remark: item.remark ?? "",
  });
  const upd = (p: Partial<typeof v>) => setV((x) => ({ ...x, ...p }));
  const save = useArrivalsCorrectItem({ mutation: { onSuccess: onDone } });
  return (
    <Modal opened onClose={onClose} title={t("arrivals.editTitle", { n: item.parc ?? "—", name: item.name ?? "—" })} size="lg">
      <div className="stack" style={{ gap: 12 }}>
        <p className="sm mu">{t("arrivals.editHelp")}</p>
        <div className="hstack" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
          {SEED_FIELDS.map(([field, label, max]) => (
            <NumberInput
              key={field}
              size="xs"
              w={110}
              min={0}
              max={max}
              hideControls
              label={t(label)}
              value={v[field] ?? ""}
              onChange={(x) => upd({ [field]: num(x) })}
            />
          ))}
        </div>
        <p className="sm mu">{t("arrivals.tkwHelp")}</p>
        <div className="hstack" style={{ gap: 10, flexWrap: "wrap" }}>
          <TextInput
            size="xs"
            w={140}
            label={t("arrivals.senderTrial")}
            value={v.sender_exp}
            maxLength={TEXT_MAX.sender_exp}
            onChange={(e) => upd({ sender_exp: e.currentTarget.value })}
          />
          <NumberInput
            size="xs"
            w={110}
            hideControls
            allowDecimal={false}
            label={t("arrivals.senderPlot")}
            value={v.sender_plot ?? ""}
            onChange={(x) => upd({ sender_plot: num(x) })}
          />
          <YearInput
            size="xs"
            w={110}
            hideControls
            label={t("arrivals.senderYear")}
            placeholder={t("arrivals.optional")}
            value={v.sender_year ?? ""}
            onChange={(x) => upd({ sender_year: num(x) })}
          />
        </div>
        <OtherNamesInput label={t("arrivals.otherNames")} value={v.other_names} onChange={(other_names) => upd({ other_names })} />
        <TextInput
          size="xs"
          maxLength={TEXT_MAX.remark}
          label={t("arrivals.remark")}
          value={v.remark}
          onChange={(e) => upd({ remark: e.currentTarget.value })}
        />
        <div className="hstack" style={{ gap: 8, justifyContent: "flex-end" }}>
          <Button variant="default" onClick={onClose}>
            {t("arrivals.cancel")}
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate({ arrival, id: item.id, data: { ...v, token: item.token } })}>
            {t("arrivals.saveItem")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
